import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';
import { IAmount } from '@/types/amount';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { endOfYear, startOfToday } from 'date-fns';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

type FormValues = {
  startDate: Date;
  endDate: Date;
  amount: IAmount;
};

type CompanyLevelLearningBudgetModalProps = Omit<
  ModalProps,
  'onClose' | 'title' | 'subTitle' | 'children'
> & {
  open: boolean;
  onClose: (data?: {
    startDate: Date;
    endDate: Date;
    amount: IAmount;
  }) => Promise<void>;
  defaultValues?: {
    startDate?: Date;
    endDate?: Date;
    amount?: IAmount;
  };
  mode?: 'edit' | 'create';
};

export function CompanyLevelLearningBudgetModal({
  open,
  onClose,
  defaultValues,
  mode = 'create',
  ...props
}: CompanyLevelLearningBudgetModalProps) {
  const schema = yup.object({
    startDate: yup
      .date()
      .typeError('Start date is required')
      .required('Start date is required'),
    endDate: yup
      .date()
      .typeError('End date is required')
      .min(yup.ref('startDate'), 'End date must be after start date')
      .required('End date is required'),
    amount: yup
      .object({
        amount: yup
          .number()
          .typeError('Amount is required')
          .required('Amount is required')
          .positive('Amount must be positive'),
        currency: yup
          .string()
          .typeError('Currency is required')
          .required('Currency is required'),
      })
      .required('Amount is required'),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      startDate: defaultValues?.startDate || startOfToday(),
      endDate: defaultValues?.endDate || endOfYear(startOfToday()),
      amount: defaultValues?.amount || { amount: 0, currency: 'EUR' },
    },
    resolver: yupResolver<FormValues>(schema),
  });

  const startDate = watch('startDate');
  const computedEndDate = endOfYear(startDate);

  useEffect(() => {
    if (startDate && computedEndDate) {
      if (getValues('endDate').getTime() !== computedEndDate.getTime()) {
        setValue('endDate', computedEndDate, { shouldValidate: true });
      }
    }
  }, [startDate, computedEndDate, setValue, getValues]);

  const onSubmit = async (data: FormValues) => {
    await onClose({
      startDate: data.startDate,
      endDate: data.endDate,
      amount: data.amount,
    });
  };

  return (
    <Modal
      {...props}
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
      title="Set Default Company Level Learning Budget"
      subTitle="Allocate learning budgets for all members for this cycle."
      actions={
        <>
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
              reset();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="company-level-learning-budget-modal-form"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Confirm
          </Button>
        </>
      }
      classes={{ paper: 'max-w-2xl' }}
    >
      <form
        id="company-level-learning-budget-modal-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack gap={3}>
          <Stack
            direction="row"
            gap={2}
            className={mode === 'edit' ? 'hidden' : ''}
          >
            <FormControl fullWidth error={!!errors.startDate}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="start-date-picker"
              >
                Start Date (UTC)
              </Typography>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    timezone="utc"
                    value={field.value}
                    onChange={date => field.onChange(date || startOfToday())}
                    format="MM/dd/yyyy"
                    disabled={mode === 'edit'}
                    slotProps={{
                      textField: { size: 'small', id: 'start-date-picker' },
                    }}
                  />
                )}
              />
              <FormHelperText>{errors.startDate?.message}</FormHelperText>
            </FormControl>
            <FormControl fullWidth error={!!errors.endDate}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="end-date-picker"
              >
                End Date (UTC)
              </Typography>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    timezone="utc"
                    value={computedEndDate}
                    onChange={() => {}}
                    disabled={true}
                    format="MM/dd/yyyy"
                    slotProps={{
                      textField: { size: 'small', id: 'end-date-picker' },
                    }}
                  />
                )}
              />
              <FormHelperText>{errors.endDate?.message}</FormHelperText>
            </FormControl>
          </Stack>
          <FormControl fullWidth error={!!errors.amount}>
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="learning-budget-amount"
            >
              Enter Learning Budget Amount
            </Typography>
            <Controller
              name="amount.amount"
              control={control}
              render={({
                field: { value, onChange, ref: inputRef, ...field },
              }) => (
                <MoneyInput
                  {...field}
                  value={integerToMoney(value)}
                  onChange={e => {
                    onChange(moneyToInteger(e.target.value));
                  }}
                  inputRef={inputRef}
                  id="learning-budget-amount"
                  decimalScale={2}
                  startAdornment={
                    <Typography
                      className={`text-2xl font-semibold ${
                        isSubmitting ? 'text-gray-400' : ''
                      }`}
                    >
                      €
                    </Typography>
                  }
                  allowNegative={true}
                  classes={{
                    root: 'flex-1 rounded-none text-center',
                    input: 'text-center text-2xl font-semibold',
                    focused: 'outline-none',
                  }}
                />
              )}
            />
            <FormHelperText>{errors.amount?.message}</FormHelperText>
          </FormControl>
        </Stack>
      </form>
    </Modal>
  );
}
